/**
 * Componente: TablaAccesosCRUD
 * 
 * Descripción:
 * Este componente administra los accesos de un usuario en forma de tabla, permitiendo realizar
 * operaciones CRUD (Crear, Leer, Actualizar y Eliminar) sobre los accesos asociados a un usuario específico.
 * 
 * Funcionalidades:
 * - Listar todos los accesos de un usuario específico filtrando los datos obtenidos desde la API `getAccesosTabla`.
 * - Permitir agregar un nuevo acceso mediante el modal `ModalTablaAccesos`.
 * - Editar los campos `Password` y `Windows` de cada acceso directamente en la tabla.
 * - Eliminar accesos mediante un botón de acción en cada fila.
 * - Actualizar la tabla automáticamente después de cualquier operación para reflejar los cambios.
 * 
 * Tecnologías y librerías utilizadas:
 * - React (Hooks: useState, useEffect)
 * - PrimeReact: Button
 * - Material UI: Modal
 * - Componente personalizado: AgGrid para tabla dinámica y edición inline
 * - Servicios API internos: getAccesosTabla, updateAccesoTabla, deleteAccesoTablaRow
 * - Modal personalizado: ModalTablaAccesos para registrar nuevos accesos
 * - confirmDialog: para consultar si queremos aplicar las nuevas actualizaciones o eliminar la fila
 * 
 * Props:
 * - userEdit: objeto del usuario cuyos accesos se están gestionando.
 * - onHide: función opcional que se ejecuta al finalizar una operación (guardar, actualizar o eliminar) para cerrar el componente o refrescar datos externos.
 * 
 * Flujo general:
 * 1. Cuando `userEdit` cambia, se llama a `getInfoAcceso` para cargar los accesos de ese usuario desde la API.
 * 2. El botón "Nuevo Acceso +" abre el modal `ModalTablaAccesos` para agregar un nuevo registro de acceso.
 * 3. La tabla muestra los accesos existentes y permite:
 *    - Edición inline de las columnas `Password` y `Windows`.
 *    - Actualización de un registro mediante el botón de lápiz, que llama a `handleUpdate`.
 *    - Eliminación de un registro mediante el botón de basura, que llama a `handleDelete`.
 * 4. Cada operación (crear, actualizar, eliminar) se mostrara un confirmDialog y luego refresca la tabla automáticamente llamando a `getInfoAcceso`.
 * 
 * Estados principales:
 * - listadoAccesosTabla: arreglo que contiene los accesos filtrados del usuario.
 * - loading: booleano que indica si los datos se están cargando.
 * - dialogTablaAcceso: booleano que controla la visibilidad del modal para agregar un acceso.
 * - selectedAcceso: objeto del acceso seleccionado para edición (puede ser null al crear uno nuevo).
 * - password: string temporal que almacena la contraseña al editar inline o desde el modal.
 * - windows: string temporal que almacena el nombre de la máquina Windows al editar inline o desde el modal.
 */

import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Modal } from '@mui/material';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { ModalTablaAccesos } from './ModalTablaAccesos/ModalTablaAccesos';
import { getAccesosTabla, updateAccesoTabla, deleteAccesoTablaRow } from '../../../../../API/IT/Usuarios/AccesosUserRequets';
import AgGrid from '../../../../../Base/Components/Tables/AgGrid/AgGrid';
import { classNames } from 'primereact/utils';


export const TablaAccesosCRUD = ({ userEdit, onHide }) => {

    /** Estados */
    const [listadoAccesosTabla, setListadoAccesosTabla] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogTablaAcceso, setDialogTablaAcceso] = useState(false);
    const [selectedAcceso, setSelectedAcceso] = useState(null);
    const [password, setPassword] = useState('');
    const [windows, setWindows] = useState('');

    /**
     * Obtiene los accesos de la tabla pra el usuario seleccionado
     * @async
     * @function getInfoAcceso
     * @returns {Promise<void>} Actualiza el estado de listadoAccesosTabla y loading
     */
    const getInfoAcceso = async () => {

        setLoading(true);

        try {
            const tablaAccesos = await getAccesosTabla();

            // 🔹 Si existe userEdit y tiene Usuario → filtra
            if (userEdit && userEdit.Usuario) {
                const filtrados = tablaAccesos.filter(
                    item => item.UserIPS === userEdit.Usuario
                );
                setListadoAccesosTabla(filtrados);
            }
            // 🔹 Si NO existe userEdit → muestra todo
            else {
                setListadoAccesosTabla(tablaAccesos);
            }

        } catch (error) {
            console.error("Error cargando accesos:", error);
        }

        setLoading(false);
    };

    // const getInfoAcceso = async () => {
    //     if (!userEdit?.Usuario) return; // Si no hay usuario seleccionado, salir

    //     setLoading(true); // Activa loading
    //     const tablaAccesos = await getAccesosTabla(); // LLamdo API
    //     const filtrados = tablaAccesos.filter( // Filtra los accesos segun el usuario seleccionado
    //         item => item?.UserIPS === userEdit.Usuario
    //     );
    //     setListadoAccesosTabla(filtrados); // Actualiza estados con accesos filtrados
    //     setLoading(false); // Desactiva loading
    // };

    /**
     * useEffect para cargar la informacion de accesos cuando cambia el usuario
     */
    useEffect(() => {
        getInfoAcceso();
    }, []);

    // useEffect(() => {
    //     if (userEdit) getInfoAcceso();
    // }, [userEdit]);

    /**
     * Abre el modal de nuevo acceso
     * @function openModal
     */
    const openModal = () => {
        setSelectedAcceso(null); // Resetea seleccion
        setDialogTablaAcceso(true); // Muestra modal
    };

    /**
     * Cierra el modal y refresca la tabla de accesos
     * @function hideModal
     */
    const hideModal = () => {
        setDialogTablaAcceso(false); // Cierra modal
        setSelectedAcceso(null); // Resetea seleccion
        getInfoAcceso(); // Refresca la tabla llamado la API getInfoAcceso()
    };

    /**
     * Actualiza un acceso existente en el backend con los datos enviados desde el frontend
     * @async
     * @function handleUpdate
     * @param {Object} rowData - Objeto de la fila seleccionada con datos del acceso
     * @returns {Promise<void>} Al recibir los datos editados dentro de la fila de la tabla, y luego se click el boton de editar envia los datos al backend, no devuelve nada 
     */
    const handleUpdate = async (rowData) => {
        const accesoData = { // Recibe los datos de la fila seleccionado, y los cambios generados 
            IdAcceso: rowData.IdAcceso,
            UserIPS: rowData.UserIPS,
            Password: password || rowData.Password,
            Windows: windows || rowData.Windows
        };

        console.log('Datos enviados al backend:', accesoData);

        try {
            const res = await updateAccesoTabla(accesoData); // Llama API, para aplicar la actualizacion de datos
            if (typeof onHide === 'function') onHide(); // Cierra el modal al escuchar que se actualizaron los datos
            getInfoAcceso(); // refresca tabla
        } catch (error) {
            console.error('Error actualizando acceso:', error);
        }
    };

    /**
     * Elimina un acceso de la tabla en el backend
     * @async
     * @function handleDelete
     * @param {Object} rowData - Objeto de la fila seleccionada con IdAcceso
     * @returns {Promise<void>} No devuelve nada, refresca tabla tras eliminar
     */
    const handleDelete = async (rowData) => {
        const accesoData = { // Recibe el IdAcceso de la fila seleccionada
            IdAcceso: rowData.IdAcceso,
        };

        console.log('IdAcceso Eliminar:', accesoData);

        try {
            const res = await deleteAccesoTablaRow(accesoData); // Llama API, para eliminar la fila seleccionada segun el IdAcceso de esta
            if (typeof onHide === 'function') onHide(); // Cierra el modal al escuchar que se elimini la fila
            getInfoAcceso(); // refresca tabla
        } catch (error) {
            console.error('Error eliminando acceso:', error);
        }
    };

    /**
  * Muestra ConfirmDialog antes de actualizar un registro
  * @param {Object} rowData 
  */
    const confirmUpdate = (rowData) => {
        confirmDialog({
            message: '¿Está seguro que desea actualizar este acceso?',
            header: 'Confirmar actualización',
            icon: 'pi pi-exclamation-triangle',
            accept: () => handleUpdate(rowData),
            reject: () => getInfoAcceso() // refresca tabla
        });
    };

    /**
     * Muestra ConfirmDialog antes de eliminar un registro
     * @param {Object} rowData 
     */
    const confirmDelete = (rowData) => {
        confirmDialog({
            message: '¿Está seguro que desea eliminar este acceso?',
            header: 'Confirmar eliminación',
            icon: 'pi pi-exclamation-triangle',
            accept: () => handleDelete(rowData),
            reject: () => { } // refresca tabla
        });
    };

    /**
     * Configuracion de la tabla
     */
    const table = {
        Data: listadoAccesosTabla,
        loading: loading,
        Columns: [
            { field: 'UserIPS', header: 'Usuario', className: 'medium', editable: true },
            { field: 'Password', header: 'Contraseña', className: 'medium', editable: true },
            { field: 'Windows', header: 'Windows', className: 'large', editable: true },
            { field: 'IdAcceso', header: 'IdAcceso', className: 'medium' },
            {
                field: 'acciones',
                header: 'Acciones',
                Format: 'Template',
                className: 'medium',
                body: (rowData) => (
                    <div className='d-flex gap-2'>
                        <Button
                            icon="pi pi-pencil"
                            className="p-button-warning p-button-text p-0"
                            tooltip="Editar"
                            type="button"
                            onClick={() => confirmUpdate(rowData)}
                        />
                        <Button
                            icon="pi pi-trash"
                            className="p-button-danger p-button-text p-0"
                            tooltip="Eliminar"
                            type="button"
                            onClick={() => confirmDelete(rowData)}
                        />
                    </div>
                )
            }
        ]
    };

    return (
        <div style={{
            height: userEdit?.Usuario ? '400px' : '95%',
            transition: 'all 0.3s ease'
        }}>
            {/* ConfirmDialog global */}
            <ConfirmDialog />

            {/* Botón para abrir modal */}
            <Button
                label="Nuevo Acceso +"
                severity="success"
                type="button"
                onClick={openModal}
            />
            {/* Tabla de accesos */}
            <AgGrid
                table={table}
                onCellValueChanged={(params) => {
                    if (params.colDef.field === 'Password') setPassword(params.data.Password);
                    if (params.colDef.field === 'Windows') setWindows(params.data.Windows);
                }}
            />
            {/* Modal para agregar nuevo acceso */}
            <ModalTablaAccesos
                visible={dialogTablaAcceso}
                onHide={hideModal}
                userEdit={userEdit}
                selectedAcceso={selectedAcceso}
            />
        </div>
    );
};

export default TablaAccesosCRUD;
